import React, { Suspense, useCallback, useEffect, useMemo } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ImageChatResult } from '@atlassian/smart-knowledge-cards/image-chat-result';
import { ImageGenerated } from '@atlassian/smart-knowledge-cards/image-generated';
import { LinkEmbed } from '@atlassian/smart-knowledge-cards/link-embed';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';
import { LoadingSkeleton as UserInlinePreviewListLoadingSkeleton } from '@atlassian/user-inline-preview/loading';
import { ErrorCard as UserPreviewErrorCard } from '@atlassian/user-profile-preview/error';
import { LoadingSkeleton as UserProfilePreviewLoadingSkeleton } from '@atlassian/user-profile-preview/loading';

import { useLuminaAnalyticsEvents } from '../../analytics/use-lumina-analytics-events';
import { useLuminaShownOnce } from '../../analytics/use-lumina-shown-once';
import { useLuminaInteraction } from '../interaction-context';
import { ProjectHighlightsElement } from '../sub-agents/components/project-highlights/project-highlights';
import { RelatedPeopleElement } from '../sub-agents/components/related-people/related-people';
import type {
	ActionElementType,
	ImageElementType,
	PeopleEntityElementType,
	PeopleEntityListElementType,
	SmartLinkElementType,
	SmartLinkListElementType,
} from '../types';
import { getUniquePeopleEntityAccountIdsFromList } from '../utils';
import { CarouselElement } from './carousel-element';
import { InferredProjectElementAdapter } from './inferred-project-element';
import { MarkdownElement } from './markdown-element';
import { PeopleEntityInlineListRelay } from './people-entity/people-entity-inline-list-relay';
import { PeopleEntityProfileRelay } from './people-entity/people-entity-profile-relay';
import { PeopleInteractionListener } from './people-entity/people-interaction-listener';
import { SmartLinkElement } from './smartlink-element';
import type { ElementRenderer, ElementTag } from './types';

const styles = cssMap({
	linkListWrapper: {
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
		width: '100%',
	},
	linkEmbedWrapper: {
		marginTop: token('space.100'),
		width: '100%',
	},
	peopleEntityWrapper: {
		marginTop: token('space.100'),
		width: '100%',
	},
});

/**
 * Adapter for smart link elements
 * Ensures all smart links are rendered as standalone cards.
 * When smartLinkRenderer is 'embed', renders a hero embed card instead.
 */
const SmartLinkElementAdapter: ElementRenderer = ({
	component,
	variant,
	isStreaming,
	sectionKey,
	containerType,
	indexInContainer,
	smartLinkRenderer,
	onSourceClick,
}) => {
	const url = (component as SmartLinkElementType).attributes?.url;
	const onInteraction = useLuminaInteraction();

	const handleEmbedLinkClick = useCallback(
		(opts: { isAuxClick?: boolean; isContextMenu?: boolean; isCopy?: boolean }) => {
			if (!url) {
				return;
			}
			onSourceClick?.({
				componentType: 'linkEmbed',
				url,
				indexInContainer,
				isAuxClick: opts.isAuxClick,
				isContextMenu: opts.isContextMenu,
				isCopy: opts.isCopy,
			});
			onInteraction?.({
				type: 'sourceActivated',
				component: 'linkEmbed',
				activation: opts.isCopy ? 'copy' : opts.isContextMenu ? 'contextMenu' : 'open',
			});
		},
		[onInteraction, onSourceClick, url, indexInContainer],
	);

	if (smartLinkRenderer === 'embed' && url) {
		return (
			<Box xcss={styles.linkEmbedWrapper}>
				<LinkEmbed
					url={url}
					onLinkClick={onSourceClick || onInteraction ? handleEmbedLinkClick : undefined}
				/>
			</Box>
		);
	}

	return (
		<SmartLinkElement
			component={component as SmartLinkElementType}
			variant={variant}
			isStreaming={isStreaming}
			sectionKey={sectionKey}
			containerType={containerType}
			indexInContainer={indexInContainer}
		/>
	);
};

/**
 * Adapter for action card elements
 * Converts action card elements into smart link elements with validation
 */
const ActionCardElementAdapter: ElementRenderer = ({
	component,
	variant,
	isStreaming,
	sectionKey,
	containerType,
	indexInContainer,
}) => {
	const actionCard = component as ActionElementType;

	// Validate component has a url and convert to smart link
	if (!actionCard.attributes?.url) {
		return null;
	}

	const smartLinkComponent: SmartLinkElementType = {
		tag: 'smart-link',
		attributes: {
			url: actionCard.attributes.url,
			title: actionCard.attributes?.title,
			type: 'action',
			pos: actionCard.attributes?.pos,
		},
	};

	return (
		<SmartLinkElement
			component={smartLinkComponent}
			variant={variant}
			isStreaming={isStreaming}
			sectionKey={sectionKey}
			containerType={containerType}
			indexInContainer={indexInContainer}
		/>
	);
};

/**
 * Adapter for related people sub-agent elements
 * Extracts related people data from the sub-agent data map
 */
const RelatedPeopleElementAdapter: ElementRenderer = ({
	subAgentDataMap,
	isStreaming,
	sectionKey,
}) => (
	<RelatedPeopleElement
		data={subAgentDataMap.relatedPeople}
		isStreaming={isStreaming}
		sectionKey={sectionKey}
	/>
);

/**
 * Adapter for project highlights sub-agent elements
 * Extracts project highlights data from the sub-agent data map
 */
const ProjectHighlightsElementAdapter: ElementRenderer = ({
	subAgentDataMap,
	isStreaming,
	sectionKey,
}) => (
	<ProjectHighlightsElement
		data={subAgentDataMap.projectHighlights}
		isStreaming={isStreaming}
		sectionKey={sectionKey}
	/>
);

/**
 * Adapter for smart link list elements
 * Renders a vertical stack of smart link cards from item children
 */

const SmartLinkListElementAdapter: ElementRenderer = ({
	component,
	onSourceClick,
	sessionId,
	source,
}) => {
	useLuminaShownOnce({ componentType: 'smartLinkList', sessionId });
	const onInteraction = useLuminaInteraction();

	const listComponent = component as SmartLinkListElementType;
	// Build url/title pairs together so the two arrays stay index-aligned after
	// filtering. Each rendered url must have a corresponding title at the same
	// index (falling back to an empty string when the title attribute is
	// missing) so `LinkList` can map a url back to its title 1:1.
	const { linkListUrls, linkListTitles } = useMemo(() => {
		const pairs = (Array.isArray(listComponent.children) ? listComponent.children : [])
			.map((item) => {
				if (typeof item === 'string') {
					return null;
				}
				const attrs = item.attributes as Record<string, string | undefined> | undefined;
				const url = attrs?.['url'];
				if (!url) {
					return null;
				}
				return { url, title: attrs?.['title'] ?? '' };
			})
			.filter((pair): pair is { title: string; url: string } => pair !== null);

		return {
			linkListUrls: pairs.map((pair) => pair.url),
			linkListTitles: pairs.map((pair) => pair.title),
		};
	}, [listComponent.children]);

	const handleLinkClick = useCallback(
		(url: string, index: number, opts?: { isAuxClick?: boolean; isContextMenu?: boolean }) => {
			onSourceClick?.({
				componentType: 'linkList',
				url,
				indexInContainer: index,
				isAuxClick: opts?.isAuxClick,
				isContextMenu: opts?.isContextMenu,
			});
			onInteraction?.({
				type: 'sourceActivated',
				component: 'linkList',
				activation: opts?.isContextMenu ? 'contextMenu' : 'open',
			});
		},
		[onInteraction, onSourceClick],
	);

	return (
		<Box xcss={styles.linkListWrapper}>
			<LinkList
				urls={linkListUrls}
				titles={linkListTitles}
				onLinkClick={onSourceClick || onInteraction ? handleLinkClick : undefined}
				source={source}
				openLinksInNewTab
			/>
		</Box>
	);
};

/**
 * ErrorBoundary fallback for the single-person UserProfilePreview.
 * Fires `operational.lumina.errored` (errorType: userProfilePreviewLoadFailed) when a
 * Relay or rendering error is caught, then renders the standard error card UI.
 */
function UserProfilePreviewErrorFallback(): React.JSX.Element {
	const { fireEvent } = useLuminaAnalyticsEvents();
	useEffect(() => {
		fireEvent('operational.lumina.errored', {
			errorType: 'userProfilePreviewLoadFailed',
			context: null,
		});
		// fireEvent is stable — safe to omit from deps
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return <UserPreviewErrorCard />;
}

/**
 * ErrorBoundary fallback for the multi-person UserInlinePreviewList.
 * Fires `operational.lumina.errored` (errorType: userInlinePreviewListLoadFailed) when a
 * Relay or rendering error is caught, then renders the standard error card UI.
 */
function UserInlinePreviewListErrorFallback(): React.JSX.Element {
	const { fireEvent } = useLuminaAnalyticsEvents();
	useEffect(() => {
		fireEvent('operational.lumina.errored', {
			errorType: 'userInlinePreviewListLoadFailed',
			context: null,
		});
		// fireEvent is stable — safe to omit from deps
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return <UserPreviewErrorCard />;
}

/**
 * Single people entity — Relay profile preview
 */
const PeopleEntityElementAdapter: ElementRenderer = ({ component, ...props }) => {
	useLuminaShownOnce({ componentType: 'peopleEntity', sessionId: props.sessionId });

	const accountId = (component as PeopleEntityElementType).attributes?.accountId;
	if (!accountId) {
		return null;
	}

	return (
		<Box xcss={styles.peopleEntityWrapper}>
			<PeopleInteractionListener>
				<ErrorBoundary fallbackRender={() => <UserProfilePreviewErrorFallback />}>
					<Suspense fallback={<UserProfilePreviewLoadingSkeleton />}>
						<PeopleEntityProfileRelay accountId={accountId} {...props} />
					</Suspense>
				</ErrorBoundary>
			</PeopleInteractionListener>
		</Box>
	);
};

/**
 * List of people entities — one Relay `userSearchV2` for all child account IDs.
 */
const PeopleEntityListElementAdapter: ElementRenderer = ({ component, source, ...props }) => {
	useLuminaShownOnce({ componentType: 'peopleEntityList', sessionId: props.sessionId });

	const listComponent = component as PeopleEntityListElementType;
	const accountIds = getUniquePeopleEntityAccountIdsFromList(listComponent);

	const inSAINFeature = source === 'luminaSAIN';
	const columnCount = inSAINFeature ? 2 : 1;
	const itemCount = inSAINFeature ? 8 : 4;

	// The list tag has been seen in the stream but is not yet closed — show the
	// skeleton immediately so there's no blank gap while parsing completes.
	if (listComponent.attributes?.loading === 'true') {
		return (
			<Box xcss={styles.peopleEntityWrapper}>
				<UserInlinePreviewListLoadingSkeleton columnCount={columnCount} itemCount={itemCount} />
			</Box>
		);
	}

	if (!accountIds.length) {
		return null;
	}

	return (
		<Box xcss={styles.peopleEntityWrapper}>
			<PeopleInteractionListener>
				<ErrorBoundary fallbackRender={() => <UserInlinePreviewListErrorFallback />}>
					<Suspense
						fallback={
							<UserInlinePreviewListLoadingSkeleton
								columnCount={columnCount}
								itemCount={itemCount}
							/>
						}
					>
						<PeopleEntityInlineListRelay
							accountIds={accountIds}
							cloudId={props.cloudId}
							orgId={props.orgId}
							requestingUserId={props.requestingUserId}
							testId="lumina-people-entity-list"
							initialVisibleCount={itemCount}
							layout={inSAINFeature ? 'grid' : 'stack'}
						/>
					</Suspense>
				</ErrorBoundary>
			</PeopleInteractionListener>
		</Box>
	);
};

/** Adapter for generated and search-retrieved image elements. */
const ImageElementAdapter: ElementRenderer = ({ component, indexInContainer, onSourceClick }) => {
	const imageComponent = component as ImageElementType;
	const { created, product, source, title, url } = imageComponent.attributes ?? {};

	if (!source) {
		return null;
	}

	if (created === 'true') {
		return <ImageGenerated imageUrl={source} altText={title ?? ''} />;
	}

	if (created === 'false') {
		return (
			<ImageChatResult
				altText={title ?? ''}
				imageUrl={source}
				product={product}
				sourceTitle={title}
				sourceUrl={url}
				onSourceClick={
					url && onSourceClick
						? () =>
								onSourceClick({
									componentType: 'imageSearch',
									indexInContainer,
									url,
								})
						: undefined
				}
			/>
		);
	}

	return null;
};

/**
 * Centralized map of element renderers
 * Maps element tags to their corresponding component references
 *
 * Components with direct references (MarkdownElement, CarouselElement) require no adaptation.
 * Components with adapters handle prop mapping/data transformation.
 */
export const elementRenderers: Record<ElementTag, ElementRenderer> = {
	'action-card': ActionCardElementAdapter,
	carousel: CarouselElement as ElementRenderer,
	image: ImageElementAdapter,
	'inferred-project': InferredProjectElementAdapter,
	md: MarkdownElement as ElementRenderer,
	'people-entity': PeopleEntityElementAdapter,
	'people-entity-list': PeopleEntityListElementAdapter,
	'project-highlights': ProjectHighlightsElementAdapter,
	'related-people': RelatedPeopleElementAdapter,
	'smart-link': SmartLinkElementAdapter,
	'smart-link-list': SmartLinkListElementAdapter,
};
